import { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface LeitorValeProps {
  ativo: boolean;
  aoLer: (codigo: string) => void;
  aoErroCamera: (mensagem: string) => void;
}

// Pedido à câmera: traseira, na maior resolução que o celular tiver ("ideal" nunca dá erro).
const CAMERA_RESOLUCAO = {
  facingMode: 'environment',
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};
// Configuração de hoje (a que sempre funcionou) — usada quando a câmera é fraca ou algo falha.
const CONFIG_HOJE = { fps: 10, qrbox: { width: 240, height: 240 } };
// Área de leitura = 80 % do menor lado da imagem.
const quadrado80 = (largura: number, altura: number) => {
  const lado = Math.floor(Math.min(largura, altura) * 0.8);
  return { width: lado, height: lado };
};

// Mede a câmera UMA vez por abertura da página e guarda o zoom.
let zoomGuardado: number | null = null;

async function medirZoom(larguraTela: number): Promise<number> {
  if (zoomGuardado !== null) return zoomGuardado;
  let menorLado = 0;
  try {
    const s = await navigator.mediaDevices.getUserMedia({ video: CAMERA_RESOLUCAO, audio: false });
    const ajustes = s.getVideoTracks()[0]?.getSettings() ?? {};
    menorLado = Math.min(ajustes.width ?? 0, ajustes.height ?? 0);
    s.getTracks().forEach((t) => t.stop());
  } catch {
    menorLado = 0;
  }
  // zoom = quantos pixels da câmera cabem em cada pixel da tela (máximo 3×).
  const z = Math.min(3, Math.floor((menorLado / (larguraTela || 360)) * 10) / 10);
  zoomGuardado = z >= 1.5 ? z : 1;
  return zoomGuardado;
}

const esperar = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Leitor de QR do vale (mesma engine html5-qrcode da portaria).
export default function LeitorVale({ ativo, aoLer, aoErroCamera }: LeitorValeProps) {
  const ativoRef = useRef(ativo);
  ativoRef.current = ativo;

  const aoLerRef = useRef(aoLer);
  aoLerRef.current = aoLer;

  const caixaRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const startPromiseRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    let desmontado = false;
    const scanner = new Html5Qrcode('barraca-qr');
    const aoDecodificar = (decodificado: string) => {
      if (!ativoRef.current) return;
      aoLerRef.current(decodificado);
    };
    const semQr = () => {
      /* frame sem QR — normal */
    };

    const abrir = async () => {
      const zoom = await medirZoom(caixaRef.current?.clientWidth ?? 360);
      if (desmontado) return;
      if (zoom > 1 && zoomRef.current) {
        zoomRef.current.style.width = `${zoom * 100}%`;
        try {
          await scanner.start(
            { facingMode: 'environment' },
            { fps: 15, disableFlip: true, qrbox: quadrado80, videoConstraints: CAMERA_RESOLUCAO },
            aoDecodificar,
            semQr,
          );
          return;
        } catch {
          // não deu com zoom: volta ao jeito de hoje
          zoomGuardado = 1;
          zoomRef.current.style.width = '100%';
          await esperar(300);
          if (desmontado) return;
        }
      }
      try {
        await scanner.start({ facingMode: 'environment' }, CONFIG_HOJE, aoDecodificar, semQr);
      } catch {
        // a câmera às vezes demora a ser liberada: tenta mais uma vez
        await esperar(700);
        if (desmontado) return;
        await scanner.start({ facingMode: 'environment' }, CONFIG_HOJE, aoDecodificar, semQr);
      }
    };

    const startPromise = abrir();
    startPromiseRef.current = startPromise;

    startPromise.catch((err) => {
      const msg =
        typeof err === 'string' ? err : (err as { message?: string })?.message || 'Câmera indisponível.';
      aoErroCamera(msg);
    });

    return () => {
      desmontado = true;
      const p = startPromiseRef.current ?? Promise.resolve();
      p.catch(() => undefined).finally(() => {
        try {
          scanner
            .stop()
            .then(() => scanner.clear())
            .catch(() => {
              /* câmera já liberada */
            });
        } catch {
          /* não estava lendo */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={caixaRef} className="relative h-full w-full overflow-hidden bg-black">
      <div ref={zoomRef} className="absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2">
        <div id="barraca-qr" className="w-full" />
      </div>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[240px] w-[240px] -translate-x-1/2 -translate-y-1/2">
        <span className="absolute left-0 top-0 h-8 w-8 border-l-4 border-t-4 border-white/70" />
        <span className="absolute right-0 top-0 h-8 w-8 border-r-4 border-t-4 border-white/70" />
        <span className="absolute bottom-0 left-0 h-8 w-8 border-b-4 border-l-4 border-white/70" />
        <span className="absolute bottom-0 right-0 h-8 w-8 border-b-4 border-r-4 border-white/70" />
      </div>
    </div>
  );
}